import React, { useRef, useState } from 'react';
import { ShieldCheck, Upload, RefreshCw, Stethoscope, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { PermissionGuard } from '@components/PermissionGuard';
import { acquirerService } from '@services/acquirerService';
import { AcquirerPIXConfiguration } from '@/types/acquirer';
import { CertificateResult } from '@/types/certificate';
import { formatDateBR } from '@pages/financial/financialUtils';
import { certificateBadgeClass, certificateStatus, daysUntilISO } from '@/utils/certificateStatus';
import { CertificateLog } from '@components/certificates/CertificateLog';
import { CertificateResultCard } from '@components/certificates/CertificateResultCard';
import { SecretField } from '@components/certificates/SecretField';

interface Props {
  config: AcquirerPIXConfiguration;
  /** Chamado após qualquer operação que altere o certificado, para recarregar a lista. */
  onChanged: () => void;
}

/**
 * Bloco de certificado mTLS da configuração de PIX.
 *
 * O certificado deixou de ser um arquivo montado no pod do PaymentHub e passou
 * a viver no Storage, enviado por aqui. Três ações:
 *
 * - Enviar/Substituir: sobe um .pfx que você já tem.
 * - Renovar: o ERP fala com o Itaú, gera o certificado novo e o coloca em uso.
 * - Testar: confere se o certificado gravado autentica no Itaú E se o
 *   PaymentHub está usando esse mesmo certificado — as duas coisas podem
 *   divergir, e é justamente aí que mora o problema difícil de enxergar.
 */
export const PIXCertificateSection: React.FC<Props> = ({ config, onChanged }) => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<'upload' | 'test' | 'renew' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CertificateResult | null>(null);
  const [confirmRenew, setConfirmRenew] = useState(false);
  // Incrementado a cada operação concluída, para o histórico recarregar.
  const [logVersion, setLogVersion] = useState(0);

  const status = certificateStatus(config.certificate_expires_at);
  const days = config.certificate_expires_at ? daysUntilISO(config.certificate_expires_at) : undefined;

  const run = async (kind: 'upload' | 'test' | 'renew', fn: () => Promise<CertificateResult>) => {
    try {
      setBusy(kind);
      setError(null);
      setResult(null);
      const res = await fn();
      setResult(res);
      setLogVersion((v) => v + 1);
      if (kind !== 'test') onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao executar a operação');
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = () => {
    if (!file) {
      setError('Escolha o arquivo .pfx do certificado');
      return;
    }
    if (!password) {
      setError('Informe a senha do certificado');
      return;
    }
    run('upload', async () => {
      const res = await acquirerService.uploadPIXCertificate(config.acquirer_pix_configuration_id, file, password);
      setFile(null);
      setPassword('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return res;
    });
  };

  const handleTest = () =>
    run('test', () => acquirerService.testPIXCertificate(config.acquirer_pix_configuration_id));

  const handleRenew = () => {
    setConfirmRenew(false);
    run('renew', () => acquirerService.renewPIXCertificate(config.acquirer_pix_configuration_id));
  };

  const expiryBadge = () => {
    if (!config.certificate_expires_at) {
      // Certificado gravado sem validade registrada não é ausência de
      // certificado: a empresa cobra normalmente, só falta o ERP conhecer a
      // data — o que o primeiro envio por aqui resolve.
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass('missing')}`}>
          <AlertTriangle size={13} />
          {config.has_certificate ? 'Validade não registrada' : 'Sem certificado'}
        </span>
      );
    }
    const text =
      status === 'expired'
        ? `Vencido em ${formatDateBR(config.certificate_expires_at)}`
        : `Válido até ${formatDateBR(config.certificate_expires_at)}`;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass(status)}`}>
        <ShieldCheck size={13} /> {text}
      </span>
    );
  };

  return (
    <div className="mt-6 pt-6 border-t border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm font-medium text-slate-800">Certificado mTLS do PIX (.pfx)</p>
        {expiryBadge()}
      </div>

      {config.certificate_uploaded_at && (
        <p className="text-xs text-slate-500 mb-3">
          Último envio em {formatDateBR(config.certificate_uploaded_at)}
          {config.certificate_uploaded_by ? ` por ${config.certificate_uploaded_by}` : ''}.
        </p>
      )}

      {config.has_certificate && (
        <div className="mb-4">
          <SecretField
            label="Senha do certificado gravado"
            value={config.certificate_pass}
            emptyLabel="Sem senha gravada"
            hint="É a senha que abre o .pfx do Storage. Serve para conferir o certificado com openssl ou curl fora do sistema."
          />
        </div>
      )}

      {/* Configuração ainda no modelo antigo: o arquivo está montado no pod do
          PaymentHub e o ERP não o alcança — nem para testar, nem para renovar. */}
      {config.certificate_path && !config.certificate_path.startsWith('https://') && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-amber-800">
            Esta configuração ainda usa um certificado montado no PaymentHub
            (<span className="font-mono text-xs">{config.certificate_path}</span>). Envie o .pfx
            abaixo para migrá-lo para o Storage — só assim é possível testar e renovar por aqui.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {result && <CertificateResultCard result={result} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Arquivo .pfx</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pfx,.p12"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
          />
        </div>
        <div>
          <Input
            label="Senha do certificado"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha do arquivo enviado"
          />
        </div>
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleUpload}
            loading={busy === 'upload'}
            disabled={busy !== null}
            icon={<Upload size={16} />}
          >
            {config.has_certificate ? 'Substituir certificado' : 'Enviar certificado'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button
          size="sm"
          variant="secondary"
          onClick={handleTest}
          loading={busy === 'test'}
          disabled={busy !== null}
          icon={<Stethoscope size={16} />}
        >
          Testar certificado
        </Button>

        {confirmRenew ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-700">
              Renovar agora no Itaú e passar a usar o certificado novo?
            </span>
            <Button size="sm" variant="danger" onClick={handleRenew}>
              Confirmar
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirmRenew(false)}>
              Cancelar
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirmRenew(true)}
            loading={busy === 'renew'}
            disabled={busy !== null}
            icon={<RefreshCw size={16} />}
          >
            Renovar certificado
          </Button>
        )}
      </div>

      <p className="text-xs text-slate-500 mt-3">
        O arquivo é validado com a senha antes de ser aceito e precisa pertencer ao Client ID desta
        configuração. A renovação só é aceita pelo Itaú nos últimos 30 dias antes do vencimento
        {days !== undefined ? ` (faltam ${days} dia(s))` : ''}. Depois de gravar, o PaymentHub é
        avisado na hora — não é preciso republicar nem reiniciar nada.
      </p>

      <CertificateLog
        fetchLogs={() => acquirerService.getPIXCertificateLogs(config.acquirer_pix_configuration_id)}
        refreshKey={logVersion}
        emptyLabel="Nenhuma operação registrada ainda para esta configuração."
      />
    </div>
  );
};

/** Envolve a seção na permissão de escrita do hub de recebimentos. */
export const PIXCertificateSectionGuarded: React.FC<Props> = (props) => (
  <PermissionGuard permission="receiving_hub.write">
    <PIXCertificateSection {...props} />
  </PermissionGuard>
);
